export class MessengerState {
	constructor(options = {}) {
		this.currentView = 'home';
		this.isOpen = false;
		this.unreadCount = 0;
		this.activeConversationId = null;

		this.conversations = [];
		this.messages = {};

		this.helpArticles = [];
		this.helpSearchQuery = '';

		this.homeChangelogItems = [];
		this.changelogItems = [];

		this.teamName = options.teamName || 'Support';
		this.teamAvatars = options.teamAvatars || [];
		this.greetingMessage = options.greetingMessage || 'Hi there 👋';
		this.welcomeMessage = options.welcomeMessage || 'How can we help?';

		this.userContext = options.userContext || null;
		this.isIdentified = false;
		this.pendingMessage = null;

		this.enableHelp = options.enableHelp !== false;
		this.enableChangelog = options.enableChangelog !== false;

		this.agentsOnline = false;
		this.onlineCount = 0;
		this.responseTime = 'Usually replies within a few minutes';

		this.typingUsers = {};

		this.isLoading = false;
		this.isLoadingMessages = false;

		this.urls = options.urls || {
			feedback: null,
			changelog: null,
			help: null,
			roadmap: null,
		};

		this._listeners = new Set();
	}

	subscribe(callback) {
		this._listeners.add(callback);
		return () => this._listeners.delete(callback);
	}

	_notify(changeType, data) {
		this._listeners.forEach((cb) => cb(changeType, data, this));
	}

	setView(view) {
		const previousView = this.currentView;
		this.currentView = view;
		this._notify('viewChange', { previousView, currentView: view });
	}

	setOpen(isOpen) {
		this.isOpen = isOpen;
		this._notify('openChange', { isOpen });
	}

	setActiveConversation(conversationId) {
		const previousConversationId = this.activeConversationId;
		this.activeConversationId = conversationId;
		this._notify('conversationChange', {
			conversationId,
			previousConversationId,
		});
	}

	setIdentified(isIdentified, userContext = null) {
		this.isIdentified = isIdentified;
		if (userContext) {
			this.userContext = { ...this.userContext, ...userContext };
		}
		this._notify('identificationChange', { isIdentified, userContext });
	}

	setConversations(conversations) {
		this.conversations = conversations;
		this._updateUnreadCount();
		this._notify('conversationsUpdate', { conversations });
	}

	addConversation(conversation) {
		this.conversations.unshift(conversation);
		this._updateUnreadCount();
		this._notify('conversationAdded', { conversation });
	}

	setMessages(conversationId, messages) {
		this.messages[conversationId] = messages;
		this._notify('messagesUpdate', { conversationId, messages });
	}

	_getMessageAttachmentsSignature(message) {
		if (
			!Array.isArray(message?.attachments) ||
			message.attachments.length === 0
		) {
			return '';
		}

		return message.attachments
			.map((att) => `${att?.type || ''}:${att?.name || ''}`)
			.join('|');
	}

	_findOptimisticMatchIndex(conversationId, incomingMessage, matchWindowMs) {
		const messages = this.messages[conversationId] || [];
		const incomingTimestamp = Date.parse(incomingMessage.timestamp);
		const incomingSignature =
			this._getMessageAttachmentsSignature(incomingMessage);

		for (let i = messages.length - 1; i >= 0; i--) {
			const candidate = messages[i];
			if (!candidate?.isOptimistic || !candidate?.isOwn) {
				continue;
			}
			if ((candidate.content || '') !== (incomingMessage.content || '')) {
				continue;
			}

			const candidateSignature =
				this._getMessageAttachmentsSignature(candidate);
			if (candidateSignature !== incomingSignature) {
				continue;
			}

			const candidateTimestamp = Date.parse(candidate.timestamp);
			if (
				Number.isNaN(incomingTimestamp) ||
				Number.isNaN(candidateTimestamp) ||
				Math.abs(incomingTimestamp - candidateTimestamp) <= matchWindowMs
			) {
				return i;
			}
		}

		return -1;
	}

	_updateConversationFromMessage(conversationId, message, countUnread) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (!conv) {
			return;
		}

		conv.lastMessage = message.content;
		conv.lastMessageTime = message.timestamp;

		if (countUnread && !message.isOwn) {
			conv.unread = (conv.unread || 0) + 1;
			this._updateUnreadCount();
		}
	}

	upsertMessage(conversationId, message, options = {}) {
		if (!this.messages[conversationId]) {
			this.messages[conversationId] = [];
		}

		const reconcileOwnOptimistic = options.reconcileOwnOptimistic === true;
		const optimisticMatchWindowMs = options.optimisticMatchWindowMs || 30000;
		const messages = this.messages[conversationId];
		const existingIndex =
			message?.id != null
				? messages.findIndex((msg) => msg?.id === message.id)
				: -1;

		if (existingIndex !== -1) {
			messages[existingIndex] = {
				...messages[existingIndex],
				...message,
				isOptimistic: false,
			};
			this._updateConversationFromMessage(
				conversationId,
				messages[existingIndex],
				false
			);
			this._notify('messagesUpdate', {
				conversationId,
				messages: [...messages],
			});
			return messages[existingIndex];
		}

		if (reconcileOwnOptimistic && message?.isOwn) {
			const optimisticIndex = this._findOptimisticMatchIndex(
				conversationId,
				message,
				optimisticMatchWindowMs
			);
			if (optimisticIndex !== -1) {
				messages[optimisticIndex] = {
					...messages[optimisticIndex],
					...message,
					isOptimistic: false,
				};
				this._updateConversationFromMessage(
					conversationId,
					messages[optimisticIndex],
					false
				);
				this._notify('messagesUpdate', {
					conversationId,
					messages: [...messages],
				});
				return messages[optimisticIndex];
			}
		}

		const storedMessage = {
			...message,
			isOptimistic: Boolean(message?.isOptimistic),
		};
		messages.push(storedMessage);
		this._updateConversationFromMessage(conversationId, storedMessage, true);
		this._notify('messageAdded', { conversationId, message: storedMessage });
		return storedMessage;
	}

	addMessage(conversationId, message) {
		return this.upsertMessage(conversationId, message);
	}

	updateConversation(conversationId, updates) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (!conv) {
			return null;
		}

		Object.assign(conv, updates);
		this._notify('conversationUpdated', { conversationId, conversation: conv });
		return conv;
	}

	markAsRead(conversationId) {
		const conv = this.conversations.find((c) => c.id === conversationId);
		if (conv && conv.unread > 0) {
			conv.unread = 0;
			this._updateUnreadCount();
			this._notify('conversationRead', { conversationId });
		}
	}

	_updateUnreadCount() {
		this.unreadCount = this.conversations.reduce(
			(sum, c) => sum + (c.unread || 0),
			0
		);
		this._notify('unreadCountChange', { count: this.unreadCount });
	}

	setHelpArticles(articles) {
		this.helpArticles = articles;
		this._notify('helpArticlesUpdate', { articles });
	}

	setHelpSearchQuery(query) {
		this.helpSearchQuery = query;
		this._notify('helpSearchChange', { query });
	}

	setHomeChangelogItems(items) {
		this.homeChangelogItems = items;
		this._notify('homeChangelogUpdate', { items });
	}

	setChangelogItems(items) {
		this.changelogItems = items;
		this._notify('changelogUpdate', { items });
	}

	getActiveConversation() {
		return this.conversations.find((c) => c.id === this.activeConversationId);
	}

	getActiveMessages() {
		return this.messages[this.activeConversationId] || [];
	}

	setTeamAvatarsFromAgents(agents) {
		if (!agents || agents.length === 0) return;

		this.teamAvatars = agents.map((agent) => {
			if (agent.picture) return agent.picture;
			return agent.full_name || '?';
		});
		this._notify('teamAvatarsUpdate', { teamAvatars: this.teamAvatars });
	}

	getFilteredHelpArticles() {
		if (!this.helpSearchQuery) {
			return this.helpArticles;
		}
		const query = this.helpSearchQuery.toLowerCase();
		return this.helpArticles.filter(
			(article) =>
				article.title.toLowerCase().includes(query) ||
				(article.description &&
					article.description.toLowerCase().includes(query))
		);
	}

	reset() {
		this.currentView = 'home';
		this.activeConversationId = null;
		this.helpSearchQuery = '';
		this._notify('reset', {});
	}
}
